// src/pages/FacturacionPresupuestos/DocumentCanvasPreview.js
import React, { useMemo, useState } from 'react';
import { FaPrint, FaWhatsapp, FaEnvelope, FaSave, FaCopy, FaPlus, FaTrash } from 'react-icons/fa';
import { numeroALetras } from '../../utils/numeroALetras';
import { TEMAS_ZEN, FUENTES_DISPONIBLES, UNIDADES_LIST } from '../../utils/plantillasDocumentos';
import { obtenerTamanoContenidoSeccion, obtenerTamanoTituloSeccion } from './tamanoTextoUtils';
import { imprimirDocumentoComercial } from './imprimirDocumentoService';
import { formatearTextoEnriquecidoHtml, tieneFormatoEnriquecido } from '../../utils/formatoTextoUtils';
import { sincronizarTextoInversionInicial } from '../../utils/inversionInicialUtils';
import './DocumentCanvasPreview.css';

const DocumentCanvasPreview = ({
    documento,
    setDocumento,
    onGuardar,
    onAbrirSendGrid,
    onConvertirDocumento,
    onCompartirWhatsApp,
    guardando = false
}) => {
    const [editandoSubtituloIdx, setEditandoSubtituloIdx] = useState(null);
    const tema = TEMAS_ZEN[documento.temaId] || TEMAS_ZEN.domus;
    const fuente = FUENTES_DISPONIBLES.find(f => f.id === documento.fuenteId) || FUENTES_DISPONIBLES[0];
    const tipoActual = (documento.tipoDocumento || 'presupuesto').toLowerCase();

    // Tamaños independientes de texto: Contenido y Títulos por sección
    const fsEmisor = obtenerTamanoContenidoSeccion(documento, 'emisor');
    const fsTituloEmisor = obtenerTamanoTituloSeccion(documento, 'emisor');

    const fsCliente = obtenerTamanoContenidoSeccion(documento, 'cliente');
    const fsTituloCliente = obtenerTamanoTituloSeccion(documento, 'cliente');

    const fsCondiciones = obtenerTamanoContenidoSeccion(documento, 'condiciones');
    const fsTituloCondiciones = obtenerTamanoTituloSeccion(documento, 'condiciones');

    // Partidas mantiene su escalado armónico conjunto según requerimiento
    const fsPartidas = obtenerTamanoContenidoSeccion(documento, 'partidas');

    const fsTotales = obtenerTamanoContenidoSeccion(documento, 'totales');
    const fsTituloTotales = obtenerTamanoTituloSeccion(documento, 'totales');

    const fsBanco = obtenerTamanoContenidoSeccion(documento, 'banco');
    const fsTituloBanco = obtenerTamanoTituloSeccion(documento, 'banco');

    const fsNotaClave = obtenerTamanoContenidoSeccion(documento, 'notaClave');
    const fsTituloNotaClave = obtenerTamanoTituloSeccion(documento, 'notaClave');

    const fsFirmas = obtenerTamanoContenidoSeccion(documento, 'firmas');
    const fsTituloFirmas = obtenerTamanoTituloSeccion(documento, 'firmas');

    // Cálculos financieros
    const { subtotal, totalDescuento, montoIva, totalFinal } = useMemo(() => {
        const sub = (documento.conceptos || []).reduce((acc, curr) => {
            const cant = Number(curr.cantidad) || 0;
            const precio = Number(curr.precioUnitario) || 0;
            return acc + (cant * precio);
        }, 0);

        const desc = Number(documento.descuento) || 0;
        const baseIva = Math.max(0, sub - desc);
        const iva = documento.aplicaIva ? (baseIva * ((Number(documento.tasaIva) || 16) / 100)) : 0;
        const total = baseIva + iva;

        return {
            subtotal: sub,
            totalDescuento: desc,
            montoIva: iva,
            totalFinal: total
        };
    }, [documento.conceptos, documento.descuento, documento.aplicaIva, documento.tasaIva]);

    const importeEnLetras = useMemo(() => {
        return numeroALetras(totalFinal, 'PESOS', 'M.N.');
    }, [totalFinal]);

    // Detalles del emisor sincronizados dinámicamente con Dirección, Contacto y RFC (Punto 1)
    const detallesEmisorTexto = useMemo(() => {
        const partes = [
            documento.emisor?.direccion,
            documento.emisor?.contactoInfo,
            documento.emisor?.rfc ? `RFC: ${documento.emisor.rfc}` : null
        ].filter(Boolean);
        return partes.length > 0 ? partes.join(' · ') : (documento.emisor?.detalles || 'Innovación, Seguridad y Gestión Integral para Condominios');
    }, [documento.emisor?.direccion, documento.emisor?.contactoInfo, documento.emisor?.rfc, documento.emisor?.detalles]);

    // Función para compartir por WhatsApp
    const handleCompartirWhatsApp = () => {
        const telefono = (documento.cliente?.telefono || '').replace(/\D/g, '');
        const tipoNombre = documento.tipoDocumento === 'presupuesto' ? 'Presupuesto Formal' :
                           documento.tipoDocumento === 'pago' ? 'Nota de Pago' : 'Remisión de Cobro';
        const clienteNom = documento.cliente?.nombre || 'Estimado(a) Cliente';
        const folioDoc = documento.folio || 'S/F';
        const totalStr = totalFinal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

        const mensaje = `Hola *${clienteNom}*, te comparto el *${tipoNombre}* con folio *${folioDoc}* por un total de *$${totalStr} MXN* emitido por *${documento.emisor?.nombre || 'Domus Habita'}*.\n\nQuedo a tu disposición para cualquier duda o confirmación. ¡Saludos cordiales!`;

        let url = `https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje)}`;
        if (telefono && telefono.length >= 10) {
            const telFinal = telefono.length === 10 ? `52${telefono}` : telefono;
            url = `https://api.whatsapp.com/send?phone=${telFinal}&text=${encodeURIComponent(mensaje)}`;
        }

        window.open(url, '_blank');
    };

    // Actualizar campos anidados de forma ágil
    const handleUpdateField = (path, valor) => {
        setDocumento(prev => {
            const copia = { ...prev };
            const keys = path.split('.');
            let actual = copia;
            for (let i = 0; i < keys.length - 1; i++) {
                if (!actual[keys[i]]) actual[keys[i]] = {};
                actual = actual[keys[i]];
            }
            actual[keys[keys.length - 1]] = valor;
            return copia;
        });
    };

    // Modificar concepto
    const handleConceptoChange = (index, campo, valor) => {
        setDocumento(prev => {
            const nuevos = [...(prev.conceptos || [])];
            nuevos[index] = { ...nuevos[index], [campo]: valor };
            return { ...prev, conceptos: nuevos };
        });
    };

    const handleAgregarConcepto = () => {
        setDocumento(prev => ({
            ...prev,
            conceptos: [
                ...(prev.conceptos || []),
                {
                    id: 'c_' + Date.now(),
                    cantidad: 1,
                    unidad: 'Servicio',
                    descripcion: 'Nuevo concepto o servicio',
                    subtitulo: 'Detalle o descripción técnica del servicio',
                    periodo: 'Único',
                    precioUnitario: 0
                }
            ]
        }));
    };

    const handleEliminarConcepto = (index) => {
        setDocumento(prev => {
            const nuevos = (prev.conceptos || []).filter((_, i) => i !== index);
            return { ...prev, conceptos: nuevos };
        });
    };

    return (
        <div className="canvas-preview-wrapper" style={{ fontFamily: fuente.fontFamily }}>
            {/* Barra flotante de acciones rápidas superior (No imprimible) */}
            <div className="no-print" style={{
                position: 'fixed',
                top: '76px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'rgba(15, 23, 42, 0.92)',
                backdropFilter: 'blur(10px)',
                padding: '8px 18px',
                borderRadius: '9999px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.15)'
            }}>
                {/* Selector Rápido de Formato de Hoja */}
                <select
                    value={documento.tamanoHoja || 'letter'}
                    onChange={(e) => handleUpdateField('tamanoHoja', e.target.value)}
                    style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        color: '#ffffff',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        outline: 'none'
                    }}
                    title="Formato de Hoja de Impresión"
                >
                    <option value="letter" style={{ color: '#0f172a' }}>📄 Carta (216 × 279 mm)</option>
                    <option value="legal" style={{ color: '#0f172a' }}>📜 Oficio (216 × 356 mm)</option>
                    <option value="a4" style={{ color: '#0f172a' }}>📋 A4 (210 × 297 mm)</option>
                </select>

                {/* Selector de Ajuste Inteligente / Escala */}
                <select
                    value={documento.ajusteImpresion || 'ajustar_1_hoja'}
                    onChange={(e) => handleUpdateField('ajusteImpresion', e.target.value)}
                    style={{
                        background: (documento.ajusteImpresion === 'ajustar_1_hoja' || !documento.ajusteImpresion) ? 'rgba(16, 185, 129, 0.35)' : 'rgba(255, 255, 255, 0.15)',
                        color: (documento.ajusteImpresion === 'ajustar_1_hoja' || !documento.ajusteImpresion) ? '#6ee7b7' : '#ffffff',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        outline: 'none'
                    }}
                    title="Ajuste de Impresión para evitar desbordes"
                >
                    <option value="ajustar_1_hoja" style={{ color: '#0f172a' }}>⚡ Ajustar a 1 Hoja (Auto-Fit)</option>
                    <option value="estandar" style={{ color: '#0f172a' }}>📑 Flujo Estándar (Multihotja)</option>
                    <option value="compacto_90" style={{ color: '#0f172a' }}>🔍 Escala 90%</option>
                    <option value="compacto_85" style={{ color: '#0f172a' }}>🔍 Escala 85%</option>
                </select>

                <button
                    type="button"
                    onClick={() => imprimirDocumentoComercial(documento)}
                    style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontWeight: '700',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                    }}
                    title="Imprimir documento o Guardar en PDF"
                >
                    <FaPrint /> Imprimir / PDF
                </button>

                <button
                    type="button"
                    onClick={() => onCompartirWhatsApp ? onCompartirWhatsApp(documento) : handleCompartirWhatsApp()}
                    style={{
                        background: '#25D366',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontWeight: '700',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                    }}
                    title="Compartir enlace o resumen por WhatsApp"
                >
                    <FaWhatsapp style={{ fontSize: '14px' }} /> WhatsApp
                </button>

                <button
                    type="button"
                    onClick={onAbrirSendGrid}
                    style={{
                        background: '#4f46e5',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontWeight: '700',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                    }}
                    title="Enviar por correo electrónico (inmediato o programado)"
                >
                    <FaEnvelope /> Enviar Correo
                </button>

                <button
                    type="button"
                    onClick={onGuardar}
                    disabled={guardando}
                    style={{
                        background: '#10b981',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontWeight: '700',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        opacity: guardando ? 0.6 : 1
                    }}
                    title="Guardar documento en la base de datos"
                >
                    <FaSave /> {guardando ? 'Guardando...' : 'Guardar'}
                </button>
            </div>

            {/* Hoja física de previsualización (WYSIWYG) */}
            <div className={`document-sheet sheet-${documento.tamanoHoja || 'letter'} ${documento.ajusteImpresion === 'ajustar_1_hoja' || !documento.ajusteImpresion ? 'sheet-ajustar-1-hoja' : ''}`}>
                {/* Línea Guía de Salto de Página Visual en Pantalla (No Imprimible) */}
                <div 
                    className="no-print page-break-indicator"
                    style={{
                        top: (documento.tamanoHoja === 'legal' ? 355.6 : documento.tamanoHoja === 'a4' ? 297 : 279.4) + 'mm'
                    }}
                >
                    <span className="page-break-badge">
                        ✂️ Fin de Hoja 1 ({documento.tamanoHoja === 'legal' ? 'Oficio 356 mm' : documento.tamanoHoja === 'a4' ? 'A4 297 mm' : 'Carta 279 mm'})
                    </span>
                </div>

                <div>
                    {/* Barra de acento con gradiente Zen */}
                    <div className="doc-top-bar" style={{ background: tema.gradienteBarra }}></div>

                    {/* Encabezado */}
                    <header className="doc-header" style={{ fontSize: `${fsEmisor}px` }}>
                        <div className="doc-emisor-block">
                            {documento.emisor?.mostrarLogo1 && (
                                (documento.emisor?.logo1Url || documento.emisor?.logoUrl) ? (
                                    <img src={documento.emisor.logo1Url || documento.emisor.logoUrl} alt="Logo Emisor 1" className="doc-logo-img" />
                                ) : (
                                    <div className="doc-logo-placeholder">Logo 1</div>
                                )
                            )}

                            {documento.emisor?.mostrarLogo2 && (
                                documento.emisor?.logo2Url ? (
                                    <img src={documento.emisor.logo2Url} alt="Logo Emisor 2" className="doc-logo-img" />
                                ) : (
                                    <div className="doc-logo-placeholder">Logo 2</div>
                                )
                            )}

                            <div className="doc-emisor-info">
                                <h1 style={{ color: tema.primario, fontSize: `${fsTituloEmisor}pt` }}>{documento.emisor?.nombre || 'Domus Habita'}</h1>
                                <div className="doc-emisor-slogan" style={{ color: tema.secundario, fontSize: `${fsEmisor}pt` }}>
                                    {documento.emisor?.slogan || 'Soluciones Residenciales & Control de Accesos'}
                                </div>
                                <div className="doc-emisor-details" style={{ fontSize: `${Math.max(7, Math.round(fsEmisor * 0.9))}pt` }}>{detallesEmisorTexto}</div>
                            </div>
                        </div>

                        <div className="doc-meta-block">
                            <span 
                                className="doc-status-badge" 
                                style={{ 
                                    background: tipoActual === 'pago' ? '#16a34a' : tipoActual === 'remision' ? '#d97706' : tema.acento, 
                                    color: '#ffffff',
                                    fontSize: `${Math.max(7, Math.round(fsEmisor * 0.85))}pt` 
                                }}
                            >
                                {documento.badgeEstado || (tipoActual === 'pago' ? '✓ PAGADO EN EFECTIVO' : tipoActual === 'remision' ? 'PENDIENTE DE PAGO' : 'VIGENTE')}
                            </span>
                            <div className="doc-main-title" style={{ color: tema.primario, fontSize: `${fsTituloEmisor}pt` }}>
                                {documento.tituloDocumento || (tipoActual === 'pago' ? 'NOTA DE PAGO' : tipoActual === 'remision' ? 'REMISIÓN DE COBRO' : 'PRESUPUESTO FORMAL')}
                            </div>
                            <table className="doc-meta-table" style={{ fontSize: `${fsEmisor}pt` }}>
                                <tbody>
                                    <tr>
                                        <td className="label">Folio:</td>
                                        <td className="val" style={{ color: tema.primario, fontWeight: 700 }}>{documento.folio}</td>
                                    </tr>

                                    {/* Metadatos específicos y limpios por categoría (Sin duplicar fecha) */}
                                    {tipoActual === 'pago' ? (
                                        <>
                                            <tr>
                                                <td className="label">Fecha de Expedición:</td>
                                                <td className="val">{documento.fechaEmision}</td>
                                            </tr>
                                            <tr>
                                                <td className="label">Fecha de Pago:</td>
                                                <td className="val" style={{ color: '#16a34a', fontWeight: 700 }}>{documento.fechaPago || documento.fechaEmision}</td>
                                            </tr>
                                            <tr>
                                                <td className="label">Estatus Saldo:</td>
                                                <td className="val" style={{ color: '#16a34a', fontWeight: 700 }}>$0.00 Adeudo (Saldado 100%)</td>
                                            </tr>
                                        </>
                                    ) : tipoActual === 'remision' ? (
                                        <>
                                            <tr>
                                                <td className="label">Fecha de Emisión:</td>
                                                <td className="val">{documento.fechaEmision}</td>
                                            </tr>
                                            <tr>
                                                <td className="label">Fecha Límite / Vencimiento:</td>
                                                <td className="val" style={{ color: '#dc2626', fontWeight: 700 }}>
                                                    {documento.fechaVencimiento || documento.fechaVigencia || '5 días hábiles'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="label">Estatus Saldo:</td>
                                                <td className="val" style={{ color: '#d97706', fontWeight: 700 }}>Pendiente de Liquidación</td>
                                            </tr>
                                        </>
                                    ) : (
                                        <>
                                            <tr>
                                                <td className="label">Fecha de Emisión:</td>
                                                <td className="val">{documento.fechaEmision}</td>
                                            </tr>
                                            <tr>
                                                <td className="label">Vigencia:</td>
                                                <td className="val" style={{ fontWeight: 600 }}>
                                                    {documento.fechaVigencia || documento.fechaVencimiento || '15 días naturales'}
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    <tr>
                                        <td className="label">Moneda:</td>
                                        <td className="val">{documento.moneda || 'MXN - Pesos Mexicanos'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </header>

                    {/* Grilla de información de Cliente y Condiciones */}
                    <section className="doc-info-grid">
                        <div className="doc-info-card" style={{ borderLeft: `3.5px solid ${tema.primario}`, fontSize: `${fsCliente}pt` }}>
                            <div className="doc-card-header">
                                <span className="doc-card-title" style={{ color: tema.primario, fontSize: `${fsCliente + 4}pt` }}>
                                    {tipoActual === 'pago' ? 'CLIENTE / PAGADOR ACREDITADO' : tipoActual === 'remision' ? 'CLIENTE / DEUDOR DEL SERVICIO' : 'CLIENTE / RECEPTOR DEL SERVICIO'}
                                </span>
                            </div>
                            <table className="doc-card-table" style={{ fontSize: `${fsCliente}pt` }}>
                                <tbody>
                                    <tr>
                                        <td className="d-label" style={{ fontSize: `${fsCliente}pt` }}>Nombre / Razón:</td>
                                        <td className="d-val" style={{ fontSize: `${fsCliente}pt` }}><strong>{documento.cliente?.nombre}</strong></td>
                                    </tr>
                                    {documento.cliente?.contacto && (
                                        <tr>
                                            <td className="d-label" style={{ fontSize: `${fsCliente}pt` }}>Atención a:</td>
                                            <td className="d-val" style={{ fontSize: `${fsCliente}pt` }}>{documento.cliente.contacto}</td>
                                        </tr>
                                    )}
                                    {documento.cliente?.direccion && (
                                        <tr>
                                            <td className="d-label" style={{ fontSize: `${fsCliente}pt` }}>Dirección:</td>
                                            <td className="d-val" style={{ fontSize: `${fsCliente}pt` }}>{documento.cliente.direccion}</td>
                                        </tr>
                                    )}
                                    {documento.cliente?.municipioEstado && (
                                        <tr>
                                            <td className="d-label" style={{ fontSize: `${fsCliente}pt` }}>Ubicación:</td>
                                            <td className="d-val" style={{ fontSize: `${fsCliente}pt` }}>{documento.cliente.municipioEstado}</td>
                                        </tr>
                                    )}
                                    {documento.cliente?.telefono && (
                                        <tr>
                                            <td className="d-label" style={{ fontSize: `${fsCliente}pt` }}>Teléfono:</td>
                                            <td className="d-val" style={{ fontSize: `${fsCliente}pt` }}>{documento.cliente.telefono}</td>
                                        </tr>
                                    )}
                                    {documento.cliente?.email && (
                                        <tr>
                                            <td className="d-label" style={{ fontSize: `${fsCliente}pt` }}>Correo:</td>
                                            <td className="d-val" style={{ fontSize: `${fsCliente}pt` }}>{documento.cliente.email}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="doc-info-card" style={{ borderLeft: `3.5px solid ${tema.secundario}`, fontSize: `${fsCondiciones}pt` }}>
                            <div className="doc-card-header">
                                <span className="doc-card-title" style={{ color: tema.secundario, fontSize: `${fsCondiciones + 4}pt` }}>
                                    {tipoActual === 'pago' 
                                        ? 'MODALIDAD DEL PAGO RECIBIDO' 
                                        : tipoActual === 'remision' 
                                            ? 'CONDICIONES DE COBRO Y RECEPCIÓN' 
                                            : 'CONDICIONES COMERCIALES Y DE SERVICIO'}
                                </span>
                            </div>
                            <table className="doc-card-table" style={{ fontSize: `${fsCondiciones}pt` }}>
                                <tbody>
                                    {tipoActual === 'presupuesto' ? (
                                        <>
                                            {/* 1. Forma de Pago */}
                                            {(documento.condiciones?.formaPago || documento.metodoPago || documento.condiciones?.terminosPago) && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Forma de Pago:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        {documento.condiciones?.formaPago || documento.metodoPago || documento.condiciones?.terminosPago}
                                                    </td>
                                                </tr>
                                            )}
                                            {/* 2. Inversión Inicial */}
                                            {documento.condiciones?.inversionInicial && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Inversión Inicial:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        {sincronizarTextoInversionInicial(documento.condiciones.inversionInicial, totalFinal)}
                                                    </td>
                                                </tr>
                                            )}
                                            {/* 3. Plazo de Instalación / Servicio */}
                                            {(documento.condiciones?.tiempoEntrega || documento.condiciones?.plazoInstalacion) && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Plazo de Instalación:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        {documento.condiciones?.tiempoEntrega || documento.condiciones?.plazoInstalacion}
                                                    </td>
                                                </tr>
                                            )}
                                            {/* 4. Renta de Sistemas */}
                                            {documento.condiciones?.rentaSistemas && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Renta de Sistemas:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        {documento.condiciones.rentaSistemas}
                                                    </td>
                                                </tr>
                                            )}
                                            {/* 5. Beneficio Exclusivo / Garantías */}
                                            {documento.condiciones?.garantia && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Beneficio / Garantía:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        {documento.condiciones.garantia}
                                                    </td>
                                                </tr>
                                            )}
                                            {/* Periodo opcional si no es N/A */}
                                            {documento.condiciones?.periodo && documento.condiciones.periodo !== 'N/A' && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Periodo:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.periodo}</td>
                                                </tr>
                                            )}
                                        </>
                                    ) : tipoActual === 'pago' ? (
                                        <>
                                            {(documento.metodoPago || documento.condiciones?.terminosPago) && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Método de Pago:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        {documento.metodoPago || documento.condiciones?.terminosPago}
                                                    </td>
                                                </tr>
                                            )}
                                            {documento.referenciaBancaria && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Ref. Bancaria:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>
                                                        <strong>{documento.referenciaBancaria}</strong>
                                                    </td>
                                                </tr>
                                            )}
                                            {documento.condiciones?.periodo && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Periodo Liquidado:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.periodo}</td>
                                                </tr>
                                            )}
                                            {documento.condiciones?.garantia && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Finiquito / Estatus:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.garantia}</td>
                                                </tr>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {documento.condiciones?.periodo && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Periodo de Servicio:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.periodo}</td>
                                                </tr>
                                            )}
                                            {documento.condiciones?.terminosPago && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Liquidación:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.terminosPago}</td>
                                                </tr>
                                            )}
                                            {documento.condiciones?.tiempoEntrega && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Plazo / Entrega:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.tiempoEntrega}</td>
                                                </tr>
                                            )}
                                            {documento.condiciones?.garantia && (
                                                <tr>
                                                    <td className="d-label" style={{ fontSize: `${fsCondiciones}pt` }}>Estatus:</td>
                                                    <td className="d-val" style={{ fontSize: `${fsCondiciones}pt` }}>{documento.condiciones.garantia}</td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Tabla de Conceptos / Partidas */}
                    <section className="doc-concepts-section" style={{ fontSize: `${fsPartidas}px` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                            <div className="doc-section-title" style={{ fontSize: `${Math.round(fsPartidas * 1.05)}px` }}>
                                {tipoActual === 'pago' 
                                    ? 'DESGLOSE DE SERVICIOS CUBIERTOS Y LIQUIDADOS' 
                                    : tipoActual === 'remision' 
                                        ? 'DESGLOSE DE SERVICIOS PRESTADOS Y CONCLUIDOS' 
                                        : 'DESGLOSE DE PARTIDAS, MATERIALES Y SERVICIOS'}
                            </div>
                            <button
                                type="button"
                                className="no-print"
                                onClick={handleAgregarConcepto}
                                style={{
                                    background: '#f1f5f9',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    color: tema.primario,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                <FaPlus /> Agregar Partida
                            </button>
                        </div>

                        <table className="doc-concepts-table" style={{ fontSize: `${fsPartidas}px` }}>
                            <thead>
                                <tr style={{ backgroundColor: tema.tablaCabecera, color: tema.tablaTexto, fontSize: `${Math.max(9, Math.round(fsPartidas * 0.95))}px` }}>
                                    <th className="col-cant text-center">CANT.</th>
                                    <th className="col-desc">DESCRIPCIÓN DE LA PARTIDA O SERVICIO</th>
                                    <th className="col-periodo text-center">
                                        {tipoActual === 'presupuesto' ? 'UNIDAD DE MEDIDA' : 'PERIODO'}
                                    </th>
                                    <th className="col-importe text-right">PRECIO UNIT. ($)</th>
                                    <th className="col-importe text-right">IMPORTE</th>
                                    <th className="no-print" style={{ width: '4%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(documento.conceptos || []).map((c, idx) => {
                                    const cant = Number(c.cantidad) || 0;
                                    const unit = Number(c.precioUnitario) || 0;
                                    const totalPartida = cant * unit;

                                    return (
                                        <tr key={c.id || idx}>
                                            <td className="col-cant text-center">
                                                <input
                                                    type="number"
                                                    value={c.cantidad}
                                                    onChange={(e) => handleConceptoChange(idx, 'cantidad', parseFloat(e.target.value) || 0)}
                                                    style={{ width: '38px', textAlign: 'center', border: 'none', background: 'transparent', fontWeight: '700', color: tema.primario, fontSize: `${fsPartidas}px` }}
                                                />
                                            </td>
                                            <td className="col-desc" style={{ textAlign: c.estilos?.textAlign || documento.estilos?.textAlign || 'left' }}>
                                                <input
                                                    type="text"
                                                    value={c.descripcion || ''}
                                                    onChange={(e) => handleConceptoChange(idx, 'descripcion', e.target.value)}
                                                    placeholder="Título del concepto..."
                                                    className="concept-title"
                                                    style={{ 
                                                        width: '100%', 
                                                        border: 'none', 
                                                        background: 'transparent', 
                                                        fontSize: `${Math.round(fsPartidas * 1.05)}px`,
                                                        textAlign: c.estilos?.textAlign || documento.estilos?.textAlign || 'left',
                                                        color: '#0f172a'
                                                    }}
                                                />
                                                {tieneFormatoEnriquecido(c.subtitulo) && editandoSubtituloIdx !== idx ? (
                                                    <div 
                                                        onClick={() => setEditandoSubtituloIdx(idx)}
                                                        title="Clic para editar la descripción"
                                                        className="concept-sub concept-sub-enriquecido"
                                                        style={{ 
                                                            width: '100%', 
                                                            fontSize: `${Math.round(fsPartidas * 0.92)}px`,
                                                            color: '#475569',
                                                            textAlign: c.estilos?.textAlign || documento.estilos?.textAlign || 'left',
                                                            whiteSpace: 'pre-wrap',
                                                            lineHeight: 1.35,
                                                            cursor: 'text',
                                                            minHeight: '18px',
                                                            background: 'transparent',
                                                            padding: '1px 0'
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: formatearTextoEnriquecidoHtml(c.subtitulo) }}
                                                    />
                                                ) : (
                                                    <textarea
                                                        value={c.subtitulo || ''}
                                                        onChange={(e) => handleConceptoChange(idx, 'subtitulo', e.target.value)}
                                                        onBlur={() => setEditandoSubtituloIdx(null)}
                                                        autoFocus={editandoSubtituloIdx === idx}
                                                        placeholder="Subtítulo o detalles del concepto..."
                                                        className="concept-sub"
                                                        rows={1}
                                                        style={{ 
                                                            width: '100%', 
                                                            border: 'none', 
                                                            background: 'transparent', 
                                                            resize: 'vertical', 
                                                            fontSize: `${Math.round(fsPartidas * 0.92)}px`,
                                                            textAlign: c.estilos?.textAlign || documento.estilos?.textAlign || 'left',
                                                            lineHeight: 1.35
                                                        }}
                                                    />
                                                )}
                                            </td>
                                            <td className="col-periodo text-center">
                                                {tipoActual === 'presupuesto' ? (
                                                    <select
                                                        value={c.unidad || 'Servicio'}
                                                        onChange={(e) => handleConceptoChange(idx, 'unidad', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            textAlign: 'center',
                                                            textAlignLast: 'center',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            fontSize: `${fsPartidas}px`,
                                                            color: '#334155',
                                                            fontWeight: 500,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {c.unidad && !UNIDADES_LIST.includes(c.unidad) && (
                                                            <option value={c.unidad}>{c.unidad}</option>
                                                        )}
                                                        {UNIDADES_LIST.map(u => (
                                                            <option key={u} value={u}>{u}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={c.periodo || ''}
                                                        onChange={(e) => handleConceptoChange(idx, 'periodo', e.target.value)}
                                                        style={{ width: '100%', textAlign: 'center', border: 'none', background: 'transparent', fontSize: `${fsPartidas}px` }}
                                                    />
                                                )}
                                            </td>
                                            <td className="col-unit text-right" style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                <div style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    fontWeight: 700,
                                                    color: '#0f172a',
                                                    fontSize: `${fsPartidas}px`,
                                                    lineHeight: 1
                                                }}>
                                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: `${fsPartidas}px`, marginRight: '1px', lineHeight: 1 }}>$</span>
                                                    <input
                                                        type="number"
                                                        value={c.precioUnitario}
                                                        onChange={(e) => handleConceptoChange(idx, 'precioUnitario', parseFloat(e.target.value) || 0)}
                                                        style={{
                                                            width: '68px',
                                                            textAlign: 'right',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            fontWeight: 700,
                                                            fontSize: `${fsPartidas}px`,
                                                            color: '#0f172a',
                                                            fontFamily: 'inherit',
                                                            padding: 0,
                                                            margin: 0,
                                                            outline: 'none',
                                                            lineHeight: 1,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                            <td className="col-importe text-right" style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', fontWeight: 700, color: '#0f172a', fontSize: `${fsPartidas}px`, lineHeight: 1 }}>
                                                ${totalPartida.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td className="no-print" style={{ textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEliminarConcepto(idx)}
                                                    style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                                                    title="Eliminar partida"
                                                >
                                                    <FaTrash style={{ fontSize: '10px' }} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>

                    {/* Resumen Financiero e Importe en Letras */}
                    <section className="doc-summary-section" style={{ fontSize: `${fsTotales}pt` }}>
                        <div className="doc-amount-words-box">
                            <div className="doc-amount-words-title" style={{ color: tema.primario, fontSize: `${fsTituloTotales}pt` }}>
                                IMPORTE TOTAL CON LETRA:
                            </div>
                            <div className="doc-amount-words" style={{ fontSize: `${fsTotales}pt` }}>{importeEnLetras}</div>
                            <div className="doc-note-small" style={{ fontSize: `${Math.max(7, Math.round(fsTotales * 0.88))}pt` }}>
                                {tipoActual === 'pago' 
                                    ? 'Comprobante de pago con finiquito total. Las especificaciones amparadas quedan respaldadas bajo el folio oficial de esta nota.'
                                    : tipoActual === 'remision'
                                        ? 'Remisión de cobro sujeta a liquidación antes de la fecha límite estipulada.'
                                        : 'Precios expresados en moneda nacional (MXN). Las especificaciones y costos quedan respaldados bajo el folio oficial de este documento.'}
                            </div>
                        </div>

                        <div>
                            <table className="doc-totals-table" style={{ fontSize: `${fsTotales}pt` }}>
                                <tbody>
                                    <tr>
                                        <td className="t-label">Subtotal:</td>
                                        <td className="t-val">
                                            ${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>

                                    {totalDescuento > 0 && (
                                        <tr>
                                            <td className="t-label" style={{ color: '#16a34a' }}>Descuento / Bonificación:</td>
                                            <td className="t-val" style={{ color: '#16a34a' }}>
                                                -${totalDescuento.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    )}

                                    {documento.aplicaIva && (
                                        <tr>
                                            <td className="t-label">IVA ({documento.tasaIva || 16}%):</td>
                                            <td className="t-val">
                                                ${montoIva.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    )}

                                    <tr className="total-row" style={{ backgroundColor: tema.totalFila, fontSize: `${fsTituloTotales}pt` }}>
                                        <td className="t-label" style={{ color: '#ffffff' }}>
                                            {tipoActual === 'pago' ? 'TOTAL PAGADO:' : tipoActual === 'remision' ? 'TOTAL A LIQUIDAR:' : 'TOTAL:'}
                                        </td>
                                        <td className="t-val" style={{ color: tema.totalMonto }}>
                                             ${totalFinal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Caja de Impacto Comunitario / Nota Clave Especial */}
                    {(documento.mostrarNotaClave !== false && documento.impactoComunitario) && (
                        <div className="doc-community-box" style={{ fontSize: `${fsNotaClave}pt` }}>
                            <strong style={{ fontSize: `${fsTituloNotaClave}pt` }}>{documento.tituloNotaClave || 'Nota Clave / Términos del Proyecto:'}</strong>{' '}
                            {tieneFormatoEnriquecido(documento.impactoComunitario) ? (
                                <span dangerouslySetInnerHTML={{ __html: formatearTextoEnriquecidoHtml(documento.impactoComunitario) }} />
                            ) : (
                                documento.impactoComunitario
                            )}
                        </div>
                    )}

                    {/* Cuentas Bancarias para Depósito */}
                    {Array.isArray(documento.cuentasBancarias) && documento.cuentasBancarias.length > 0 && (
                        <div className="doc-bank-box" style={{ fontSize: `${fsBanco}pt` }}>
                            <div className="doc-bank-title" style={{ fontSize: `${fsTituloBanco}pt` }}>
                                {tipoActual === 'pago' ? 'Cuenta Receptora de Depósito / Registro Bancario:' : 'Datos Bancarios para Depósito o Transferencia:'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {documento.cuentasBancarias.map((cb, idx) => (
                                    <div 
                                        key={idx} 
                                        style={{ 
                                            borderBottom: idx < documento.cuentasBancarias.length - 1 ? '1px dashed #cbd5e1' : 'none', 
                                            paddingBottom: idx < documento.cuentasBancarias.length - 1 ? '4px' : '0' 
                                        }}
                                    >
                                        <strong>Banco:</strong> {cb.banco || 'Banco'} | <strong>Beneficiario:</strong> {cb.titular || cb.beneficiario || 'Domus Habita'} | <strong>CLABE:</strong> {cb.clabe || 'N/D'} {cb.cuenta ? `| Cuenta: ${cb.cuenta}` : ''} {cb.tarjeta ? `| Tarjeta: ${cb.tarjeta}` : ''}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Firmas de Autorización y Conformidad (Punto 1: Opcional para Cliente) */}
                    <div className="doc-signatures-wrap" style={{ fontSize: `${fsFirmas}pt` }}>
                        <div className={`doc-sig-grid ${documento.incluirFirmaCliente === false ? 'sig-single-emisor' : ''}`}>
                            <div className="doc-sig-cell">
                                <div className="doc-sig-img-wrap">
                                    {(documento.firmas?.emisorFirmaUrl || documento.firmaUrl) && (
                                        <img src={documento.firmas?.emisorFirmaUrl || documento.firmaUrl} alt="Firma Emisor" className="doc-sig-pic" />
                                    )}
                                </div>
                                <div className="doc-sig-line"></div>
                                <div className="doc-sig-name" style={{ fontSize: `${fsTituloFirmas}pt` }}>{documento.firmas?.emisorNombre || 'C. Andrés Jonnatan Wong Feregrino'}</div>
                                <div className="doc-sig-sub" style={{ fontSize: `${fsFirmas}pt` }}>
                                    {documento.firmas?.emisorCargo || (tipoActual === 'pago' ? 'Receptor del Pago / Domus Habita Soluciones Integrales' : tipoActual === 'remision' ? 'Representante / Domus Habita Soluciones Integrales' : 'Prestador del Servicio / Domus Habita Soluciones Integrales')}
                                </div>
                            </div>

                            {documento.incluirFirmaCliente !== false && (
                                <div className="doc-sig-cell">
                                    <div className="doc-sig-img-wrap">
                                        {documento.firmas?.receptorFirmaUrl && (
                                            <img src={documento.firmas.receptorFirmaUrl} alt="Firma Receptor" className="doc-sig-pic" />
                                        )}
                                    </div>
                                    <div className="doc-sig-line"></div>
                                    <div className="doc-sig-name" style={{ fontSize: `${fsTituloFirmas}pt` }}>{documento.firmas?.receptorNombre || 'Representante Acreditado'}</div>
                                    <div className="doc-sig-sub" style={{ fontSize: `${fsFirmas}pt` }}>
                                        {documento.firmas?.receptorCargo || (tipoActual === 'pago' ? 'Emisor del Pago / Acreditado' : tipoActual === 'remision' ? 'Recepción y Compromiso de Liquidación' : 'Aceptación y Visto Bueno del Condominio')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Pie de Página */}
                <footer className="doc-footer">
                    <div>{documento.emisor?.nombre || 'Domus Habita'} — Folio Oficial: {documento.folio}</div>
                    <div>Página 1 de 1</div>
                    <div>Generado con Domus Habita Cloud | {new Date().toLocaleDateString('es-MX')}</div>
                </footer>
            </div>
        </div>
    );
};

export default DocumentCanvasPreview;
